import type {
  CompanyRequest,
  CompanyResponse,
  ExternalOrgCommercialTermsResponse,
  OrganizationDetailsResponse,
  OrganizationResponse,
  PageResponseCompanyResponse,
} from '@/api/data-contracts';
import useClientsService from '@/features/company/api/clients.service';
import useCompaniesService from '@/features/company/api/companies.service';
import { queryKeys } from '@/react-query/constants';
import type { FeatureQueryOptions } from '@/shared/api';

import { companyApi } from './companyApi';

const { keys } = companyApi;
type ExtraQueryOptions<TData> = Omit<FeatureQueryOptions<TData>, 'queryKey' | 'queryFn'>;

export const useCompaniesListQuery = (
  request: CompanyRequest,
  enabled = true,
  options?: ExtraQueryOptions<PageResponseCompanyResponse>,
) => {
  const { getCompanies } = useCompaniesService();
  return companyApi.useFeatureQuery({
    queryKey: keys.list(request),
    queryFn: (signal) => getCompanies({ request }, signal),
    enabled,
    ...options,
  });
};

export const useCompanyDetailsQuery = (
  companyId: string,
  enabled = true,
  options?: ExtraQueryOptions<CompanyResponse | undefined>,
) => {
  const { getCompanyDetails } = useCompaniesService();
  return companyApi.useFeatureQuery({
    queryKey: [queryKeys.companyDetails, companyId],
    queryFn: (signal) => getCompanyDetails(companyId, signal),
    enabled: enabled && !!companyId,
    ...options,
  });
};

/** Returns `OrganizationResponse` for the given organization id (`[queryKeys.organization, organizationId]`). */
export const useOrganizationQuery = (
  organizationId: string,
  enabled = true,
  options?: ExtraQueryOptions<OrganizationResponse>,
) => {
  const { showOrganization } = useCompaniesService();
  return companyApi.useFeatureQuery({
    queryKey: [queryKeys.organization, organizationId],
    queryFn: (signal) => showOrganization(organizationId, signal),
    enabled: enabled && !!organizationId,
    ...options,
  });
};

/** Returns `OrganizationDetailsResponse` for the current user's organization (`[queryKeys.organization, 'my']`). */
export const useMyOrganizationQuery = (
  enabled = true,
  options?: ExtraQueryOptions<OrganizationDetailsResponse>,
) => {
  const { getMyOrganization } = useCompaniesService();
  return companyApi.useFeatureQuery({
    queryKey: [queryKeys.organization, 'my'],
    queryFn: (signal) => getMyOrganization(signal),
    enabled,
    ...options,
  });
};

/** The Location's commercial terms with one advertiser organization (`[externalOrgCommercialTerms, id]`). */
export const useExternalOrgCommercialTermsQuery = (
  advertiserOrganizationId: string | undefined,
  enabled = true,
  options?: ExtraQueryOptions<ExternalOrgCommercialTermsResponse>,
) => {
  const { getExternalOrgCommercialTerms } = useClientsService();
  return companyApi.useFeatureQuery({
    queryKey: [queryKeys.externalOrgCommercialTerms, advertiserOrganizationId],
    queryFn: (signal) => getExternalOrgCommercialTerms(advertiserOrganizationId as string, signal),
    enabled: enabled && !!advertiserOrganizationId,
    ...options,
  });
};
